//C:\Users\pakin\OneDrive\Ambiente de Trabalho\MyCoachServer\frontend\src\Contexts\authContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Cria o contexto de autenticação
const AuthContext = createContext();

// Provedor de autenticação
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ao carregar o componente, verifica se há um usuário no localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const lastActivity = localStorage.getItem('lastActivity');

    const THIRTY_MINUTES = 30 * 60 * 1000;

    if (storedUser && storedToken) {
      const isSessionExpired = lastActivity && (Date.now() - parseInt(lastActivity) > THIRTY_MINUTES);

      if (isSessionExpired) {
        console.log("AuthContext - Sessão expirada por inatividade (offline).");
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('lastActivity');
        setUser(null);
      } else {
        try {
          const userData = JSON.parse(storedUser);
          console.log("AuthContext - Carregando usuário:", userData);

          // Configure axios with token
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Refresh last activity on successful restore
          localStorage.setItem('lastActivity', Date.now().toString());

          setUser(userData);
        } catch (error) {
          console.error("Erro ao analisar dados do usuário:", error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('lastActivity');
        }
      }
    }
    setLoading(false);
  }, []);

  // Função para fazer login
  const login = (userData, token) => {
    // Garante que selectedSport está incluído nos dados do usuário
    const userToStore = {
      ...userData,
      selectedSport: userData.selectedSport || null
    };

    console.log("AuthContext - Login com usuário:", userToStore);
    localStorage.setItem('user', JSON.stringify(userToStore));
    localStorage.setItem('lastActivity', Date.now().toString());

    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    setUser(userToStore);
  };

  // Função para fazer logout com redirecionamento
  const logout = () => {
    // Limpa o localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');

    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];

    // Atualiza o estado
    setUser(null);

    // Redireciona para a página de login
    window.location.replace('/login');
  };

  // Adiciona uma função para verificar se o usuário está autenticado
  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('token');
  };

  // Adiciona uma função para verificar o desporto do usuário
  const getUserSport = () => {
    return user?.selectedSport?.toLowerCase() || null;
  };



  // Revised approach: updateUser state directly
  const updateUser = (newUserData) => {
    console.log("AuthContext - Updating user data:", newUserData);
    localStorage.setItem('user', JSON.stringify(newUserData));
    setUser(newUserData);
  };

  // Idle Timer & Session Persistence Logic
  useEffect(() => {
    let idleTimer;
    let throttleTimer;

    const resetTimer = () => {
      // Logic to auto-logout locally
      if (idleTimer) clearTimeout(idleTimer);

      if (user) {
        idleTimer = setTimeout(() => {
          console.log("Auto-logout due to inactivity");
          logout();
        }, 1800000); // 30 minutes

        // Logic to update localStorage throttle (every 1 minute)
        // This ensures if the user closes the browser, we have a relatively recent timestamp
        const now = Date.now();
        if (!throttleTimer || (now - throttleTimer > 60000)) {
          localStorage.setItem('lastActivity', now.toString());
          throttleTimer = now;
        }
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];

    if (user) {
      resetTimer(); // Initialize timer on mount if user exists
      events.forEach(event => window.addEventListener(event, resetTimer));
    }

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [user]);

  const value = React.useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    getUserSport,
    updateUser
  }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar o contexto de autenticação
export const useAuth = () => useContext(AuthContext);
